import routerHelper from '$$routerHelper';
import InlineEditors from './inlineEditors';

export const NO_SELECTED = -1;
export const PART_SELECTED = 0;
export const FULL_SELECTED = 1;

class MassSelect {
  constructor() {
    this.instance = null;
    this.selectedTaskIds = {};
  }

  init(instance) {
    this.instance = instance;
    this.selectedTaskIds = {};
  }

  disableGantt(disable = true) {
    const disableClass = 'masschange-disable';

    const container = document.querySelector('.dhx_gantt_container');

    if (!container) {
      return;
    }

    if (disable) {
      !container.classList.contains(disableClass) && container.classList.add(disableClass);
    } else {
      container.classList.contains(disableClass) && container.classList.remove(disableClass);
    }
  }

  enable() {
    this.instance.config.readonly = true;
    this.disableGantt(true);
    this.instance.render();
    InlineEditors.disableEditing();
  }

  disable() {
    this.selectedTaskIds = {};
    this.instance.config.readonly = false;
    this.instance.render();
    this.disableGantt(false);
    InlineEditors.enableEditing();
  }

  removeSelectedTaskIds(taskIds) {
    taskIds.forEach(taskId => delete this.selectedTaskIds[taskId]);
  }

  isTaskSelected(taskId) {
    return this.selectedTaskIds[taskId]?.isSelected;
  }

  selectTask(taskId, includeChildren = true) {
    const depth = includeChildren ? -1 : 0;
    const tasksToSelect = this.selectTaskRecursive(taskId, depth);
    let updatedParents = [];

    if (tasksToSelect.length > 0) {
      updatedParents = this.updateSelectedParents(taskId);
    }

    if (tasksToSelect.length > 0) {
      this.instance.callEvent('onMassSelectStateChange', [this.selectedTaskIds]);
      this.updateGridCheckboxes(taskId, tasksToSelect, updatedParents);
    }
  }

  unselectTask(taskId, includeChildren = true) {
    const depth = includeChildren ? -1 : 1;
    const tasksToUnselect = this.unselectTasksRecursive(taskId, depth);
    let updatedParents = [];

    if (tasksToUnselect.length > 0) {
      updatedParents = this.updateUnselectedParents(taskId);
    }

    if (tasksToUnselect.length > 0) {
      this.instance.callEvent('onMassSelectStateChange', [this.selectedTaskIds]);
      this.updateGridCheckboxes(taskId, tasksToUnselect, updatedParents);
    }
  }

  updateGridCheckboxes(idTask, tasks, parents) {
    const maxTaskCount = 3;

    if (tasks.length < maxTaskCount) {
      tasks.forEach(id => this.instance.refreshTask(id));
      setTimeout(() => parents.forEach(parentId => this.instance.refreshTask(parentId)), 0);

      return;
    }

    this.instance.render();
  }

  getEntrySelectionState(idTask) {
    if (!this.selectedTaskIds[idTask] || !this.selectedTaskIds[idTask].isSelected) {
      return NO_SELECTED;
    }

    if (this.selectedTaskIds[idTask].isSelected && this.selectedTaskIds[idTask].allSelected) {
      return FULL_SELECTED;
    }

    return PART_SELECTED;
  }

  selectTaskRecursive(idTask, deep = 0) {
    let res = [];

    if (!this.isValidSelectionTask(idTask)) {
      return [];
    }

    if (!this.selectedTaskIds[idTask]) {
      this.selectedTaskIds[idTask] = {};
    }

    const taskEntry = this.selectedTaskIds[idTask];

    taskEntry.isSelected = true;

    const childIds = [];

    this.instance.getChildren(idTask).forEach(id => {
      // skip buttons
      if (typeof id === 'string' && id.includes('buttons')) return;
      // after d&d in gantt, the getChildren() can return string types
      const idChild = typeof id === 'number' ? id : parseInt(id, 10);

      if (this.isValidSelectionTask(idChild, idTask)) {
        childIds.push(idChild);
      }
    });

    if (typeof taskEntry.nChildren === 'undefined') {
      taskEntry.nChildren = childIds.length;
    }

    taskEntry.allSelected = (deep === 0) ? taskEntry.nChildren === taskEntry.nSelected : true;
    taskEntry.nSelected = (deep === 0) ? childIds.filter(idChild => !!this.selectedTaskIds[idChild]).length : childIds.length;

    res = [idTask];

    if (deep === 0) {
      return res;
    }

    return childIds.reduce((result, idChild) => result.concat(this.selectTaskRecursive(idChild, deep - 1)), res);
  }

  updateSelectedParents(idTask) {
    const { parent } = this.instance.getTask(idTask);
    const res = [];

    if (!parent) {
      return [];
    }

    if (!this.selectedTaskIds[idTask].allSelected) {
      return [];
    }

    let parentSelEntry = this.selectedTaskIds[parent];

    if (!parentSelEntry) {
      parentSelEntry = this.selectedTaskIds[parent] = {
        isSelected: false,
        allSelected: false,
        // !FIXME: performance
        nChildren: this.instance.getChildren(parent).filter(entry => this.isValidSelectionTask(entry)).length,
        nSelected: 1,
      };

      return [parent];
    }

    if (parentSelEntry.nSelected < parentSelEntry.nChildren) {
      parentSelEntry.nSelected += 1;
    }

    if (
      parentSelEntry.nChildren === parentSelEntry.nSelected
      && parentSelEntry.isSelected === true
    ) {
      parentSelEntry.allSelected = true;

      if (parentSelEntry.nSelected > 0) {
        res.push(parent);
      } else {
        this.unselectTasksRecursive(parent);
      }

      // traverse and update parents in cycle
      let parentOfParentEntry = this.instance.getTask(parent);
      let parentOfParentSelEntry = parentOfParentEntry && this.selectedTaskIds[parentOfParentEntry.parent];

      while (
        parentOfParentSelEntry && parentOfParentSelEntry.isSelected
        && parentOfParentSelEntry.nChildren === parentOfParentSelEntry.nSelected
      ) {
        parentOfParentSelEntry.allSelected = true;
        res.push(parentOfParentEntry.parent);

        parentOfParentEntry = this.instance.getTask(parentOfParentEntry.parent);
        parentOfParentSelEntry = parentOfParentEntry && this.selectedTaskIds[parentOfParentEntry.parent];
      }
    }

    return res;
  }

  updateUnselectedParents(idTask) {
    const { parent } = this.instance.getTask(idTask);

    const res = [];

    const parentSelEntry = this.selectedTaskIds[parent];

    if (!parentSelEntry) {
      return [];
    }

    parentSelEntry.nSelected -= 1;

    if (
      parentSelEntry.isSelected === true
      && parentSelEntry.allSelected === true
    ) {
      parentSelEntry.allSelected = false;
      res.push(parent);
      // if (parentSelEntry.nSelected > 0) {
      //   res.push(parent);
      // } else {
      //   this.unselectTasksRecursive(parent);
      // }

      // traverse and update parents in cycle
      let parentOfParentEntry = this.instance.getTask(parent);
      let parentOfParentSelEntry = parentOfParentEntry && this.selectedTaskIds[parentOfParentEntry.parent];

      while (parentOfParentSelEntry && parentOfParentSelEntry.allSelected /* && parentOfParentSelEntry.isSelected &&  */) {
        parentOfParentSelEntry.allSelected = false;
        res.push(parentOfParentEntry.parent);

        parentOfParentEntry = this.instance.getTask(parentOfParentEntry.parent);
        parentOfParentSelEntry = parentOfParentEntry && this.selectedTaskIds[parentOfParentEntry.parent];
      }
    }

    // if (parentSelEntry.isSelected === true
    //   && parentSelEntry.allSelected === false) {
    //   this.unselectTasksRecursive(parent);
    // }

    return res;
  }

  unselectTasksRecursive(idTask, deep = 1) {
    let res = [];

    if (!this.isValidSelectionTask(idTask) || deep === 0) {
      return [];
    }

    if (this.selectedTaskIds[idTask] && deep === 1 && this.selectedTaskIds[idTask].nChildren > 0) {
      this.selectedTaskIds[idTask].isSelected = false;
      this.selectedTaskIds[idTask].allSelected = false;

      res = [idTask];
    } else if (this.selectedTaskIds[idTask]) {
      delete this.selectedTaskIds[idTask];
      res = [idTask];
    }

    return this.instance.getChildren(idTask).reduce((result, idChild) => result.concat(this.unselectTasksRecursive(idChild, deep - 1)), res);
  }

  isValidSelectionTask(idTask, parentId) {
    const taskInGantt = parentId ? this.instance.getTask(parentId) : null;
    const isOpenedParent = parentId && taskInGantt && taskInGantt.type === 'project' && taskInGantt.$open;
    const taskEntry = this.instance.isTaskExists(idTask) && this.instance.getTask(idTask);

    if (!taskEntry) return false;

    const currentRoute = routerHelper.getCurrentRoute();

    if (currentRoute.name === 'project' && currentRoute.params.mode === 'list') {
      const isCurrentProject = taskEntry.gantt_id === this.instance.config.gantt_id;
      const isRightEntity = ['task', 'project', 'milestone'].indexOf(taskEntry.type) !== -1;

      return isCurrentProject && isRightEntity;
    }

    const inFlt = isOpenedParent ? this.instance.isTaskVisible(idTask) : true;

    return ['task', 'project', 'milestone'].indexOf(taskEntry.type) !== -1 && inFlt;
  }
}

export default new MassSelect();
